import { useTranslation } from 'react-i18next'
import { Bot } from 'lucide-react'
import { formatAbsoluteDate, formatRelativeDate } from '../lib/timelineDate'
import { TimelineRow } from './TimelineRow'

interface TimelineMessageProps {
  text: string
  createdAt: string
  /** `true` cuando el autor es el propio usuario que mira la pantalla. */
  isOwn: boolean
  isAutomatic: boolean
  /** Nombre del perfil del panel; `null` cuando el autor no tiene ficha. */
  authorName?: string | null
  withRail?: boolean
}

/**
 * Mensaje del hilo: burbuja con avatar, autor y fecha. Conserva las tres
 * rotulaciones que ya distinguía el hilo suelto: «Tú» para lo propio,
 * «Automático» con icono de bot, y el equipo de soporte cuando el comentario
 * llega sin nombre de autor.
 */
export function TimelineMessage({
  text,
  createdAt,
  isOwn,
  isAutomatic,
  authorName,
  withRail = true,
}: TimelineMessageProps) {
  const { t, i18n } = useTranslation()

  const author = isAutomatic
    ? t('tickets.detail.automatic')
    : isOwn
      ? t('tickets.detail.you')
      : (authorName ?? t('tickets.detail.supportTeam'))

  const marker = (
    <span className="grid size-8 place-items-center rounded-full bg-primary/10 text-primary ring-4 ring-white">
      {isAutomatic ? (
        <Bot className="size-4" />
      ) : (
        <span className="text-xs font-bold">{author.charAt(0)}</span>
      )}
    </span>
  )

  return (
    <TimelineRow marker={marker} withRail={withRail}>
      <div
        className={
          isOwn
            ? 'rounded-lg rounded-tl-none border border-primary/15 bg-primary/[0.06] p-3'
            : 'rounded-lg rounded-tl-none border border-black/5 bg-neutral-50 p-3'
        }
      >
        <p className="text-xs text-ink-muted">
          <span className="font-semibold text-ink">{author}</span>{' '}
          <time dateTime={createdAt} title={formatAbsoluteDate(createdAt)}>
            · {formatRelativeDate(createdAt, i18n.language)}
          </time>
        </p>
        <p className="mt-1 whitespace-pre-wrap break-words text-sm">{text}</p>
      </div>
    </TimelineRow>
  )
}
